import { useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Eye, EyeOff, AlertCircle, Eraser } from "lucide-react";

export default function Login({ admin = false }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [lastError, setLastError] = useState("");
  const emailRef = useRef(null);
  const pwdRef = useRef(null);

  const clearFields = () => {
    setEmail("");
    setPassword("");
    setLastError("");
    setShowPwd(false);
    setTimeout(() => emailRef.current?.focus(), 50);
    toast("Campos limpos. Digite novamente sem copiar e colar.");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // Normaliza identificador (email ou CPF) e senha (remove espaços invisíveis).
    // Detecta automaticamente: se contém só dígitos/pontos/traços, trata como CPF (apenas dígitos).
    let cleanIdentifier = (email || "").trim();
    const looksLikeCpf = /^[\d.\-\s]+$/.test(cleanIdentifier) && cleanIdentifier.replace(/\D/g, "").length >= 11;
    if (looksLikeCpf) {
      cleanIdentifier = cleanIdentifier.replace(/\D/g, ""); // só dígitos
    } else {
      cleanIdentifier = cleanIdentifier.toLowerCase();
    }
    const cleanPwd = (password || "").trim();

    if (!cleanIdentifier || !cleanPwd) {
      setLastError("Preencha e-mail/CPF e senha antes de entrar.");
      return;
    }

    setLoading(true);
    setLastError("");
    try {
      const u = await login(cleanIdentifier, cleanPwd);
      setErrorCount(0);
      toast.success(`Bem-vindo(a), ${u.full_name?.split(" ")[0]}!`);
      const isStaff = ["admin_master", "operador_evento", "comercial"].includes(u.role);
      if (admin && !isStaff) {
        toast.error("Esta conta não tem acesso administrativo.");
        return;
      }
      // Redireciona pra tela inicial APROPRIADA para cada role:
      //   - operador_evento → leitor de QR (única tela que pode ver)
      //   - comercial → inscrições/leads
      //   - admin_master → dashboard
      //   - aluno → home
      if (u.role === "operador_evento") navigate("/admin/leitor-qr", { replace: true });
      else if (u.role === "comercial") navigate("/admin/leads", { replace: true });
      else if (u.role === "admin_master") navigate("/admin/dashboard", { replace: true });
      else navigate(location.state?.from?.pathname || "/home", { replace: true });
    } catch (err) {
      setErrorCount((c) => c + 1);
      const status = err?.response?.status;
      const rawDetail = err?.response?.data?.detail || err?.message || "Falha no login";
      // Mensagens amigáveis para problemas de infraestrutura
      let friendlyMsg = rawDetail;
      if (status === 404 || /Network Error|Request failed with status code 404|status code 50[0-9]/i.test(String(rawDetail))) {
        friendlyMsg = "Servidor temporariamente indisponível. Aguarde alguns segundos e tente novamente.";
      } else if (status === 401) {
        friendlyMsg = "E-mail/CPF ou senha incorretos. Verifique e tente novamente.";
      } else if (status === 403) {
        friendlyMsg = rawDetail; // já vem amigável (ex.: conta bloqueada)
      }
      setLastError(friendlyMsg);
      toast.error(friendlyMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-hero-radial bg-white">
      <div className="max-w-[460px] mx-auto px-5 pt-10 pb-16">
        <div className="flex justify-center mb-7">
          <Logo size={44} />
        </div>
        <Card className="rounded-2xl border-[hsl(var(--border))] shadow-sm">
          <CardContent className="p-6">
            <h1 className="font-display text-2xl font-semibold text-[hsl(var(--primary))]">
              {admin ? "Acesso administrativo" : "Entrar na sua conta"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {admin ? "Painel de gestão CEAP BRASIL" : "Plataforma oficial do aluno CEAP BRASIL"}
            </p>

            {/* form com autocomplete=off para impedir auto-fill antigo do navegador */}
            <form onSubmit={onSubmit} className="mt-5 space-y-4" autoComplete="off" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="ceap-login-email">E-mail ou CPF</Label>
                <Input
                  id="ceap-login-email"
                  ref={emailRef}
                  name="ceap_login_email_xyz"
                  type="text"
                  inputMode="email"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@exemplo.com  ou  000.000.000-00"
                  data-testid="auth-email-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Você pode entrar com seu <strong>e-mail</strong> ou seu <strong>CPF</strong> (apenas números, com ou sem pontos).
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ceap-login-pwd">Senha</Label>
                <div className="relative">
                  <Input
                    id="ceap-login-pwd"
                    ref={pwdRef}
                    name="ceap_login_pwd_xyz"
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    autoCorrect="off"
                    spellCheck="false"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                    aria-label="Mostrar/ocultar senha"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  💡 Clique no olho para ver o que está digitando.
                </p>
              </div>

              {lastError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-900 text-xs">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium">{lastError}</p>
                    {errorCount >= 2 && (
                      <p className="mt-1 text-red-700">
                        Já tentou {errorCount} vezes. <strong>Provável causa</strong>: navegador colando senha antiga.
                        Clique em <strong>Limpar campos</strong> abaixo e digite novamente sem copiar/colar.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading}
                data-testid="auth-submit-button"
              >
                {loading ? "Entrando…" : "Entrar"}
              </Button>

              {errorCount >= 1 && (
                <button
                  type="button"
                  onClick={clearFields}
                  className="w-full flex items-center justify-center gap-1.5 text-xs text-[hsl(var(--accent))] hover:underline py-1"
                  data-testid="clear-fields-btn"
                >
                  <Eraser className="w-3.5 h-3.5" />
                  Limpar campos e tentar novamente
                </button>
              )}
            </form>

            {!admin && (
              <div className="mt-5 flex items-center justify-between text-sm">
                <Link to="/forgot-password" className="text-[hsl(var(--accent))] hover:underline" data-testid="link-forgot">
                  Esqueci a senha
                </Link>
                <Link to="/register" className="text-[hsl(var(--primary))] font-medium hover:underline" data-testid="link-register">
                  Criar conta
                </Link>
              </div>
            )}
            {admin && (
              <p className="mt-5 text-xs text-muted-foreground text-center">
                <Link to="/login" className="text-[hsl(var(--accent))] hover:underline">
                  Entrar como aluno
                </Link>
              </p>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-[11px] text-muted-foreground mt-6">
          © {new Date().getFullYear()} CEAP BRASIL • Centro de Estudos da Administração Pública
        </p>
        <p className="text-center text-[10px] text-muted-foreground mt-2">
          <Link to="/politica-privacidade" className="hover:underline">Política de Privacidade</Link>
          <span className="mx-1.5">•</span>
          <Link to="/termos-de-uso" className="hover:underline">Termos de Uso</Link>
        </p>
      </div>
    </div>
  );
}
